import React from "react";
import { useT } from "@/lib/LanguageContext";
import { interpolate } from "../../../i18n";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

/**
 * The capture form itself, PRESENTATIONAL only: no submit, no state that reaches the
 * server. The admin Forms page renders it live as the fields are toggled (design §E.4);
 * the public embed (`GET /api/public/crm/forms/{form_key}`, lane B) renders the very same
 * markup for real, so a field added here is a field the visitor sees — the two must never
 * drift into two different forms wearing the same name.
 *
 * `interactive`: the admin preview keeps every button inert and shows the spec's worked
 * inline error ("Check your email address") permanently, so the practitioner sees what a
 * visitor's mistake looks like without having to make one.
 *
 * TWO STEPS, both always visible here (owner requirement, 2026-09-30: "in order to
 * submit a form the user has to be logged in", implemented as email-ownership proof via
 * a 6-digit code — `modules/crm/routers/public.py`'s `_EMBED_JS`). The real embed shows
 * step 2 only after step 1's "Send code" succeeds; this STATIC preview shows both at
 * once, stacked, so the owner sees the whole visitor journey in one glance rather than
 * having to imagine step 2 from a one-line description.
 */
export default function CaptureFormView({ practiceName, host, fields, consentText, interactive = false }) {
  const t = useT("crm");
  const consent = interpolate(consentText || "", { practice_name: practiceName || "" });

  return (
    <div data-testid={CRM.captureFormView} className="rounded-2xl border border-eh-border-soft bg-white p-6">
      <p className="text-xs text-eh-muted">{t("forms.previewCaption", { host: host || "" })}</p>
      <h2 className="mt-1 font-display text-2xl text-eh-ink">{t("forms.previewHeading")}</h2>

      {/* Step 1: the lead's own details, ending in "Send code" rather than a submit. */}
      <form className="mt-4 space-y-3" onSubmit={(e) => e.preventDefault()}>
        <div>
          <label className={LABEL}>{t("forms.fieldName")} *</label>
          <input readOnly={!interactive} className="w-full rounded-xl border border-eh-border-soft bg-white px-3 py-2 text-sm" />
        </div>
        <div>
          <label className={LABEL}>{t("forms.fieldEmail")} *</label>
          <input readOnly={!interactive} className="w-full rounded-xl border border-red-400 bg-white px-3 py-2 text-sm" />
          <p role="alert" className="mt-1 text-xs text-red-700">{t("forms.previewEmailError")}</p>
        </div>
        {fields?.phone && (
          <div>
            <label className={LABEL}>{t("forms.fieldPhone")}</label>
            <input readOnly={!interactive} className="w-full rounded-xl border border-eh-border-soft bg-white px-3 py-2 text-sm" />
          </div>
        )}
        {fields?.message && (
          <div>
            <label className={LABEL}>{t("forms.fieldMessage")}</label>
            <textarea readOnly={!interactive} rows={3} className="w-full rounded-xl border border-eh-border-soft bg-white px-3 py-2 text-sm" />
          </div>
        )}
        <label className="flex items-start gap-2 text-xs text-eh-ink">
          <input type="checkbox" disabled={!interactive} className="mt-0.5 h-4 w-4 accent-eh-sage-deep" />
          <span>{consent}</span>
        </label>
        <button type="submit" disabled className="btn-primary text-sm w-full justify-center opacity-60 cursor-not-allowed">
          {t("forms.previewSend")}
        </button>
      </form>

      {/* Step 2: the email-ownership code — a SEPARATE <form>, exactly like the real
          embed (`_EMBED_JS`'s own step1/step2), never nested inside step 1's. */}
      <div className="mt-6 border-t border-eh-border-soft pt-4">
        <h3 className="font-display text-lg text-eh-ink">{t("forms.previewStep2Heading")}</h3>
        <p className="mt-1 text-xs text-eh-muted">{t("forms.previewStep2Body")}</p>
        <form className="mt-3 space-y-3" onSubmit={(e) => e.preventDefault()}>
          <div>
            <label className={LABEL}>{t("forms.fieldCode")} *</label>
            <input readOnly={!interactive} inputMode="numeric" maxLength={6}
                  className="w-full rounded-xl border border-eh-border-soft bg-white px-3 py-2 text-sm" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled className="btn-primary text-sm justify-center opacity-60 cursor-not-allowed">
              {t("forms.previewSubmit")}
            </button>
            <button type="button" disabled className="btn-ghost text-sm justify-center opacity-60 cursor-not-allowed">
              {t("forms.previewResendCode")}
            </button>
            <button type="button" disabled className="btn-ghost text-sm justify-center opacity-60 cursor-not-allowed">
              {t("forms.previewChangeEmail")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
